import { MessageBar, MessageBarType } from "@fluentui/react";
import React from "react";
import { LanguageContext } from "../../utilities/LocalizationModule";

export interface IScanErrorsMessageBarProps {
  errors: string[];
  /** Files the scan refused to parse (e.g. over the size cap) - actionable by the author. */
  skipped?: string[];
  onDismiss: () => void;
}

/**
 * Persistent surface for the "Scan for custom fields" command. Scan failures and skipped
 * templates are actionable (fix the template, shrink the file, get an owner to provision
 * the column) and often multi-file - a toast that dismisses itself after seconds is the
 * wrong vehicle. Mirrors UnfilledPlaceholdersMessageBar: lives in the content body,
 * stays until dismissed, replaced by the next scan, or the user changes directory.
 * Severity follows the content: errors make it an error bar, skips alone a warning.
 */
export const ScanErrorsMessageBar = React.memo(({ errors, skipped, onDismiss }: IScanErrorsMessageBarProps) => {
  const languageStrings = React.useContext(LanguageContext);

  // Deduplicated defensively: the lists key the <li>s.
  const uniqueErrors = React.useMemo(() => Array.from(new Set(errors ?? [])), [errors]);
  const uniqueSkipped = React.useMemo(() => Array.from(new Set(skipped ?? [])), [skipped]);

  if (!uniqueErrors.length && !uniqueSkipped.length) {
    return null;
  }

  const listStyle: React.CSSProperties = { margin: "4px 0 0", paddingLeft: 20, maxHeight: 160, overflowY: "auto" };

  return (
    <MessageBar
      messageBarType={uniqueErrors.length > 0 ? MessageBarType.error : MessageBarType.warning}
      isMultiline={true}
      onDismiss={onDismiss}
      dismissButtonAriaLabel="Close"
      // The bar shares a fixed-height flex column with the grid (see the viewport in
      // ContentBodyFilesFolders); it must keep its natural height and let the grid shrink.
      styles={{ root: { flexShrink: 0 } }}
    >
      {uniqueErrors.length > 0 && <>
        <div>{languageStrings.ScanCustomFieldsFailed}</div>
        {/* Capped: a whole-library scan can report many files; the bar must never push the
            file list off-screen. */}
        <ul style={listStyle}>
          {uniqueErrors.map(error => <li key={error}>{error}</li>)}
        </ul>
      </>}
      {uniqueSkipped.length > 0 && <>
        <div>{languageStrings.ScanCustomFieldsSkippedHeader}</div>
        <ul style={listStyle}>
          {uniqueSkipped.map(entry => <li key={entry}>{entry}</li>)}
        </ul>
      </>}
    </MessageBar>
  );
});

ScanErrorsMessageBar.displayName = "ScanErrorsMessageBar";
